import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import type { TaskListDefinition } from '@techgitter/contracts';
import { slugify } from '../../utils/slugify';
import { HelpHint } from '../admin/HelpHint';
import { FormField } from '../panel/FormField';
import { PRIMARY_BTN_SX } from '../../styles/designSystem';
import '../admin/EditorCard.css';

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as const;

interface CreateTaskListDialogProps {
  open: boolean;
  existingIds: string[];
  onClose: () => void;
  onCreate: (taskList: TaskListDefinition) => void;
}

export const CreateTaskListDialog: React.FC<CreateTaskListDialogProps> = ({
  open,
  existingIds,
  onClose,
  onCreate,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [taskListId, setTaskListId] = useState('');
  const [description, setDescription] = useState('');
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);

  useEffect(() => {
    if (!open) {
      setDisplayName('');
      setTaskListId('');
      setDescription('');
      setIdManuallyEdited(false);
    }
  }, [open]);

  const handleDisplayNameChange = (value: string) => {
    setDisplayName(value);
    if (!idManuallyEdited) {
      setTaskListId(slugify(value));
    }
  };

  const handleIdChange = (value: string) => {
    setTaskListId(value);
    setIdManuallyEdited(true);
  };

  const trimmedId = taskListId.trim();
  const trimmedName = displayName.trim();
  const isDuplicate = existingIds.includes(trimmedId);
  const isValid = trimmedId.length > 0 && trimmedName.length > 0 && !isDuplicate;

  const handleCreate = () => {
    if (!isValid) return;
    onCreate({
      taskListId: trimmedId,
      displayName: trimmedName,
      description: description.trim() || undefined,
      schemaVersion: 1,
      sections: [],
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
        <Stack direction="row" alignItems="center">
          Create Task List
          <HelpHint
            size="medium"
            text={
              <Box sx={{ maxWidth: 360 }}>
                A <strong>task list</strong> is a reusable checklist that can
                be referenced from TemplateTaskConfigs or from other task
                lists. Two flavours of list to plan for:
                <br />
                <br />
                <strong>Atomic</strong> — a generic procedure that doesn't
                depend on anything else (e.g. <em>wipe-windows</em>,{' '}
                <em>setup-arm-windows</em>). Pure inline sections; no
                references. These are the building blocks.
                <br />
                <br />
                <strong>Composite (a.k.a. bundle)</strong> — combines atomic
                lists (and optionally a few inline sections) into a higher-
                level checklist (e.g. <em>windows-new</em> referencing{' '}
                <em>wipe-windows</em> + <em>setup-arm-windows</em> +{' '}
                <em>install-base-office-suite</em>). Cycles are detected at
                resolution time, so you don't have to worry about a bundle
                accidentally referencing itself.
              </Box>
            }
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Display Name">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  /* eslint-disable-next-line jsx-a11y/no-autofocus */
                  autoFocus
                />
              </FormField>
              <div className="editor-hint" style={{ marginTop: 6 }}>
                Human-readable name shown in pickers and on tickets.
              </div>
            </Box>
            <HelpHint
              text={
                <Box sx={{ maxWidth: 320 }}>
                  Convention: <em>&lt;Platform&gt; &lt;Resource&gt;
                  &lt;Action&gt;</em>
                  <br />
                  <br />
                  Examples:
                  <br />• <strong>Windows New Setup</strong>
                  <br />• <strong>Windows Reallocate Setup</strong>
                  <br />• <strong>Mac New Setup</strong>
                  <br />• <strong>M365 User Setup</strong>
                  <br />• <strong>M365 User Offboard</strong>
                  <br /><br />
                  For atomic procedures, drop the platform-resource and just
                  describe the action: <em>Wipe Windows</em>,{' '}
                  <em>Install Base Office Suite</em>.
                </Box>
              }
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Task List ID">
                <input
                  type="text"
                  value={taskListId}
                  onChange={(e) => handleIdChange(e.target.value)}
                  style={MONO}
                />
              </FormField>
              <div
                className={`editor-hint${isDuplicate ? ' editor-hint--error' : ''}`}
                style={{ marginTop: 6 }}
              >
                {isDuplicate
                  ? 'This ID already exists'
                  : 'Auto-generated from name. Edit to customise. Cannot be changed after creation.'}
              </div>
            </Box>
            <HelpHint
              text={
                <Box sx={{ maxWidth: 360 }}>
                  Slug used to reference this list from TemplateTaskConfigs
                  and from other task lists. <strong>Immutable</strong> once
                  saved — references break if you'd rename.
                  <br />
                  <br />
                  Convention:{' '}
                  <em>&lt;platform&gt;-&lt;resource&gt;-&lt;action&gt;</em>{' '}
                  (lowercase, hyphen-separated):
                  <br />• <code>device-windows-new</code>
                  <br />• <code>device-windows-reallocate</code>
                  <br />• <code>device-mac-new</code>
                  <br />• <code>device-iphone-new</code>
                  <br />• <code>m365-user-setup</code>
                  <br />• <code>m365-user-offboard</code>
                  <br /><br />
                  For <strong>atomic</strong> procedures, drop the platform-
                  resource and just describe the action:
                  <br />• <code>wipe-windows</code>
                  <br />• <code>setup-arm-windows</code>
                  <br />• <code>install-base-office-suite</code>
                  <br /><br />
                  For <strong>customer-specific</strong> variants, append a
                  short customer code as a fourth segment:
                  <br />• <code>m365-user-setup-acme</code>
                  <br />• <code>device-windows-new-globex</code>
                  <br /><br />
                  Rules:
                  <br />• lowercase, 1–63 chars
                  <br />• letters, digits, dash (<code>-</code>), underscore
                  (<code>_</code>) — pick hyphen for new lists
                  <br />• must start with a letter or digit
                </Box>
              }
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              <FormField label="Description">
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormField>
            </Box>
            <HelpHint
              text={
                <Box sx={{ maxWidth: 320 }}>
                  Short, scannable summary of what this list covers. Shown in
                  admin tables and reference pickers. Two lines max.
                  <br />
                  <br />
                  Good: <em>Wipe + reimage a Windows laptop for return to
                  inventory.</em>
                  <br />
                  Bad: <em>The list of all the things you need to do…</em>{' '}
                  (long, generic).
                </Box>
              }
            />
          </Box>

          {isDuplicate && (
            <Alert severity="error">
              A task list with ID "{trimmedId}" already exists.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#888' }}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disableElevation disabled={!isValid} sx={PRIMARY_BTN_SX}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
